import { useEffect, useState } from "react";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const TYPE_OPTIONS = [
  { value: "download", label: "Digital Download" },
  { value: "toolkit", label: "Toolkit" },
  { value: "course", label: "Course" },
  { value: "membership", label: "Membership" },
];

const DIVISION_OPTIONS = [
  { value: "main", label: "Main" },
  { value: "bh", label: "BH Consulting" },
  { value: "hr", label: "HR Consulting" },
  { value: "continuum", label: "Continuum" },
];

const PERIOD_PRESETS = [
  { value: 30, label: "Monthly (30 days)" },
  { value: 90, label: "Quarterly (90 days)" },
  { value: 365, label: "Annual (365 days)" },
];

function toSlug(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export default function ProductFormDialog({ open, mode, product, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    id: "", slug: "", type: "download", name: "", tagline: "", description: "",
    price: "", features: "", division: "main", period_days: 30, is_free: false, require_org: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [autoSlug, setAutoSlug] = useState(true);
  const [courseUpload, setCourseUpload] = useState({ uploading: false, msg: "" });

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit && product) {
      setForm({
        id: product.id,
        slug: product.slug || "",
        type: product.type,
        name: product.name || "",
        tagline: product.tagline || "",
        description: product.description || "",
        price: String(product.price ?? ""),
        features: (product.features || []).join("\n"),
        division: product.division || "main",
        period_days: product.period_days ?? 30,
        is_free: !!product.is_free,
        require_org: !!product.require_org,
      });
      setAutoSlug(false);
    } else {
      setForm({
        id: "", slug: "", type: "download", name: "", tagline: "", description: "",
        price: "", features: "", division: "main", period_days: 30, is_free: false, require_org: false,
      });
      setAutoSlug(true);
    }
  }, [open, isEdit, product]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onNameChange = (v) => {
    if (autoSlug && !isEdit) {
      const prefix = { download: "dl", toolkit: "tk", course: "cr", membership: "mem" }[form.type] || "p";
      update({ name: v, id: `${prefix}-${toSlug(v)}` });
    } else {
      update({ name: v });
    }
  };

  const onTypeChange = (t) => {
    if (autoSlug && !isEdit && form.name) {
      const prefix = { download: "dl", toolkit: "tk", course: "cr", membership: "mem" }[t] || "p";
      update({ type: t, id: `${prefix}-${toSlug(form.name)}` });
    } else {
      update({ type: t });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      price: form.is_free ? 0 : (parseFloat(form.price) || 0),
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      division: form.division,
      is_free: !!form.is_free,
      require_org: !!form.require_org,
    };
    if (form.type === "membership") payload.period_days = parseInt(form.period_days, 10) || 30;
    if (isEdit && form.slug) payload.slug = form.slug.trim().toLowerCase();
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/products/${form.id}`, payload);
        onSaved && onSaved(data);
      } else {
        const { data } = await api.post("/admin/products", {
          ...payload, id: form.id.trim(), type: form.type,
        });
        onSaved && onSaved(data);
      }
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white max-h-[92vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--navy)]">
            {isEdit ? "Edit product" : "Add new product"}
          </DialogTitle>
          <DialogDescription className="text-[var(--muted)]">
            {isEdit
              ? "Update this product. Changes take effect immediately."
              : "Add a new offering to the Academy. It will appear in the store once saved."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="field">
              <label className="field-label">Type</label>
              <select
                value={form.type}
                onChange={(e) => onTypeChange(e.target.value)}
                disabled={isEdit}
                data-testid="pf-type"
              >
                {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {isEdit && <span className="text-[10px] text-[var(--muted)] mt-1">Type cannot be changed after creation</span>}
            </div>
            <div className="field">
              <label className="field-label">Division</label>
              <select value={form.division} onChange={(e) => update({ division: e.target.value })} data-testid="pf-division">
                {DIVISION_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="field-label">Name</label>
            <input required value={form.name} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Clinical Documentation Playbook" data-testid="pf-name" />
          </div>

          <div className="field">
            <label className="field-label">Product ID (slug)</label>
            <input
              required
              value={form.id}
              onChange={(e) => { setAutoSlug(false); update({ id: e.target.value.toLowerCase() }); }}
              disabled={isEdit}
              placeholder="e.g. dl-clinical-documentation"
              pattern="^[a-z0-9](?:[a-z0-9-]{1,62})[a-z0-9]$"
              data-testid="pf-id"
            />
            <span className="text-[10px] text-[var(--muted)] mt-1">
              {isEdit ? "ID is immutable" : "Kebab-case only. Auto-filled from the name — edit if needed."}
            </span>
          </div>

          {isEdit && (
            <div className="field">
              <label className="field-label">URL Slug (SEO-friendly)</label>
              <input
                value={form.slug}
                onChange={(e) => update({ slug: e.target.value.toLowerCase() })}
                placeholder={`e.g. compensation-benefits (defaults to id: ${form.id})`}
                pattern="^[a-z0-9](?:[a-z0-9-]{1,78})[a-z0-9]$"
                data-testid="pf-slug"
              />
              <span className="text-[10px] text-[var(--muted)] mt-1">
                Public URL becomes <code className="text-[var(--navy)]">/academy/{form.slug || form.id}</code>.
                Old ID URL keeps working via 301 redirect — safe to shorten anytime.
              </span>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="field">
              <label className="field-label">Price (USD)</label>
              <input required={!form.is_free} disabled={form.is_free} type="number" min="0" step="0.01" value={form.is_free ? "0.00" : form.price} onChange={(e) => update({ price: e.target.value })} data-testid="pf-price" />
            </div>
            {form.type === "membership" && (
              <div className="field">
                <label className="field-label">Billing period</label>
                <select value={form.period_days} onChange={(e) => update({ period_days: e.target.value })} data-testid="pf-period">
                  {PERIOD_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  <option value={730}>Bi-annual (730 days)</option>
                </select>
              </div>
            )}
          </div>

          {(form.type === "download" || form.type === "toolkit" || form.type === "course") && (
            <div className="flex gap-4 p-4 bg-[var(--cream)] border border-[var(--line)]">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_free}
                  onChange={(e) => update({ is_free: e.target.checked, require_org: e.target.checked ? true : form.require_org })}
                  data-testid="pf-is-free"
                />
                <span className="font-bold text-[var(--navy)]">Free download</span>
              </label>
              {form.is_free && (
                <span className="text-xs text-[var(--muted)] leading-relaxed">
                  Organization will be required at claim (mandatory).
                </span>
              )}
            </div>
          )}

          {isEdit && form.type === "course" && (
            <div className="p-4 bg-[var(--cream)] border border-[var(--line)] space-y-3" data-testid="pf-course-upload">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-[var(--navy)] text-sm">Course content (Rise / Articulate export)</p>
                  <p className="text-xs text-[var(--muted)] mt-1">
                    Upload the <code className="text-[10px] bg-white px-1 py-0.5">.zip</code> of your Rise Web export.
                    We'll extract it and gate the launch link to paid students.
                  </p>
                  {product?.course_uploaded_at && (
                    <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> {product.course_file_count} files uploaded
                    </p>
                  )}
                </div>
                <label className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--navy)] bg-white hover:bg-[var(--navy)] hover:text-white cursor-pointer transition-colors shrink-0"
                       data-testid="pf-course-upload-label">
                  {courseUpload.uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {courseUpload.uploading ? "Extracting…" : (product?.course_uploaded_at ? "Replace zip" : "Upload zip")}
                  <input
                    type="file" accept=".zip,application/zip"
                    className="hidden"
                    data-testid="pf-course-upload-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setCourseUpload({ uploading: true, msg: "" });
                      try {
                        const fd = new FormData(); fd.append("file", f);
                        const { data } = await api.post(
                          `/admin/products/${form.id}/upload-course`, fd,
                          { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
                        );
                        setCourseUpload({ uploading: false, msg: `✓ ${data.uploaded} files uploaded${data.skipped ? ` (${data.skipped} skipped)` : ""}` });
                        if (onSaved) onSaved();
                      } catch (err) {
                        setCourseUpload({ uploading: false, msg: `✗ ${formatApiError(err)}` });
                      } finally {
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
              </div>
              {courseUpload.msg && (
                <p className={`text-xs ${courseUpload.msg.startsWith("✓") ? "text-emerald-700" : "text-red-600"}`}
                   data-testid="pf-course-upload-msg">{courseUpload.msg}</p>
              )}
            </div>
          )}

          <div className="field">
            <label className="field-label">Tagline</label>
            <input value={form.tagline} onChange={(e) => update({ tagline: e.target.value })} placeholder="One-line summary that appears on cards" data-testid="pf-tagline" />
          </div>

          <div className="field">
            <label className="field-label">Description</label>
            <textarea rows={4} value={form.description} onChange={(e) => update({ description: e.target.value })} placeholder="Paragraph shown on the product detail page." data-testid="pf-description" />
          </div>

          <div className="field">
            <label className="field-label">Features (one per line)</label>
            <textarea rows={4} value={form.features} onChange={(e) => update({ features: e.target.value })} placeholder="48-point audit checklist&#10;Editable PDF + Word&#10;Corrective action template" data-testid="pf-features" />
          </div>

          {error && <p className="text-red-700 text-xs" data-testid="pf-error">{error}</p>}

          <DialogFooter className="pt-4 border-t border-[var(--line)]">
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost !py-2 !px-4 !text-[11px]" data-testid="pf-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-navy !py-2 !px-4 !text-[11px]" data-testid="pf-submit">
              {saving ? <><Loader2 className="animate-spin mr-2" size={12} /> Saving…</> : (isEdit ? "Save changes" : "Create product")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
